<script setup>
import { ref } from 'vue';

const props = defineProps({
    url: { type: String, required: true },
    title: { type: String, required: true }
});

const copied = ref(false);
const shareOpen = ref(false);

const shareOptions = [
    {
        name: 'Twitter',
        link: (url, title) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`
    },
    {
        name: 'Facebook',
        link: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    },
    {
        name: 'LinkedIn',
        link: (url, title) => `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
    }
];

async function share() {
    if (navigator.share) {
        try {
            await navigator.share({
                title: props.title,
                url: props.url
            });
        } catch (err) {
            console.warn('Share canceled or failed:', err);
        }
    } else {
        shareOpen.value = true;
    }
}

function copyLink() {
    navigator.clipboard.writeText(props.url).then(() => {
        copied.value = true;
        shareOpen.value = false;
        setTimeout(() => (copied.value = false), 2000);
    });
}
</script>

<template>
    <div class="relative">
        <div v-if="shareOpen" class="flex flex-row gap-3 overflow-x-auto">
            <button class="bg-white px-3 py-2 text-blue border border-blue rounded flex items-center gap-2" @click="shareOpen = !shareOpen">
                <svg viewBox="0 0 24 24" width="15" height="15">
                    <path
                        d="M14.8199 12.6917L25.0944 2.46269C25.2113 2.34748 25.3042 2.21011 25.3676 2.0586C25.4311 1.90709 25.4637 1.74446 25.4637 1.58019C25.4637 1.41591 25.4311 1.25328 25.3676 1.10177C25.3042 0.950258 25.2113 0.812896 25.0944 0.697687C24.8573 0.463002 24.5373 0.331381 24.2039 0.331381C23.8704 0.331381 23.5505 0.463002 23.3134 0.697687L13.0479 10.9177L2.68646 0.539687C2.56886 0.422613 2.42938 0.329875 2.27599 0.266767C2.12259 0.203658 1.95827 0.171416 1.79243 0.17188C1.45749 0.172818 1.13664 0.30692 0.900464 0.544687C0.664288 0.782453 0.532132 1.10441 0.533068 1.43972C0.534005 1.77504 0.667958 2.09625 0.905458 2.33269L11.2589 12.6987L0.8685 23.0397C0.751772 23.155 0.65909 23.2924 0.595832 23.4439C0.532574 23.5954 0.5 23.758 0.5 23.9222C0.5 24.0864 0.532574 24.2489 0.595832 24.4004C0.65909 24.5519 0.751772 24.6893 0.8685 24.8047C1.1056 25.0394 1.42558 25.171 1.759 25.171C2.09243 25.171 2.4124 25.0394 2.6495 24.8047L13.0309 14.4727L23.3503 24.8047C23.5872 25.0411 23.9081 25.1737 24.2426 25.1732C24.5771 25.1728 24.8976 25.0393 25.1338 24.8022C25.37 24.5651 25.5024 24.2438 25.5019 23.9089C25.5015 23.5741 25.3682 23.2531 25.1313 23.0167L14.8199 12.6917Z"
                        fill="currentColor"
                    />
                </svg>
            </button>

            <a
                v-for="option in shareOptions"
                :key="option.name"
                :href="option.link(url, title)"
                target="_blank"
                rel="noopener noreferrer"
                class="bg-blue px-3 py-2 text-white rounded flex items-center gap-2 whitespace-nowrap"
            >
                {{ option.name }}
            </a>

            <button class="bg-blue px-3 py-2 text-white rounded flex items-center gap-2 whitespace-nowrap" @click="copyLink">
                {{ copied ? 'Copied!' : 'Copy Link' }}
            </button>
        </div>

        <button v-if="!shareOpen" class="bg-blue px-3 py-2 text-white rounded flex items-center gap-2 shrink-0" @click="share">
            <svg viewBox="0 0 24 24" width="20" height="20">
                <path
                    d="M8.33097 7.05704L8.33058 7.05742C8.23642 7.14846 8.12407 7.22114 7.99979 7.27092C7.8755 7.32071 7.74195 7.34653 7.60687 7.3467C7.47178 7.34688 7.33816 7.32142 7.21372 7.27195C7.08929 7.22249 6.97673 7.1501 6.8823 7.0593L6.74498 7.20212L6.8823 7.0593C6.78789 6.96852 6.71347 6.86115 6.66291 6.74355C6.61236 6.62597 6.58654 6.50025 6.58672 6.37352C6.5869 6.24678 6.61307 6.12113 6.66396 6.00368C6.71486 5.88621 6.78958 5.77902 6.88426 5.68848L6.88465 5.6881L11.4808 1.26867L11.4808 1.26867C11.5751 1.17802 11.6874 1.10572 11.8116 1.05626C11.9358 1.0068 12.0691 0.98125 12.204 0.98125C12.3389 0.98125 12.4722 1.0068 12.5964 1.05626C12.7206 1.10572 12.8329 1.17802 12.9272 1.26867L12.9272 1.26867L17.5233 5.6881L17.5237 5.68848C17.6184 5.77902 17.6931 5.88621 17.744 6.00368C17.7949 6.12113 17.8211 6.24678 17.8213 6.37352C17.8215 6.50025 17.7956 6.62597 17.7451 6.74355C17.6945 6.86115 17.6201 6.96852 17.5257 7.0593C17.4313 7.1501 17.3187 7.22249 17.1943 7.27195C17.0698 7.32142 16.9362 7.34688 16.8011 7.3467C16.666 7.34653 16.5325 7.32071 16.4082 7.27092C16.2839 7.22114 16.1716 7.14846 16.0774 7.05742L16.077 7.05705L13.5614 4.63815L13.2228 4.31255V4.78232V17.5782C13.2228 17.8333 13.1174 18.0797 12.9272 18.2626C12.7367 18.4458 12.4767 18.55 12.204 18.55C11.9313 18.55 11.6713 18.4458 11.4808 18.2626C11.2906 18.0797 11.1852 17.8333 11.1852 17.5782V4.78232V4.31255L10.8466 4.63815L8.33097 7.05704Z"
                    fill="currentColor"
                />
                <path
                    d="M21.5469 21.2937H21.7469V21.0938V12.1094C21.7469 11.8542 21.8522 11.6078 22.0425 11.4249C22.233 11.2417 22.493 11.1375 22.7656 11.1375C23.0383 11.1375 23.2983 11.2417 23.4888 11.4249C23.679 11.6078 23.7844 11.8542 23.7844 12.1094V21.4844C23.7844 21.9467 23.5934 22.3918 23.2508 22.7213C22.9079 23.051 22.4413 23.2375 21.9531 23.2375H2.45312C1.96497 23.2375 1.49831 23.051 1.15544 22.7213C0.812825 22.3918 0.621875 21.9467 0.621875 21.4844V12.1094C0.621875 11.8542 0.727223 11.6078 0.91746 11.4249C1.10796 11.2417 1.36796 11.1375 1.64062 11.1375C1.91329 11.1375 2.17329 11.2417 2.36379 11.4249C2.55403 11.6078 2.65937 11.8542 2.65937 12.1094V21.0938V21.2937H2.85938H21.5469Z"
                    fill="currentColor"
                />
            </svg>
            {{ $t('plain_share') }}
        </button>
    </div>
</template>
